
import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Code, Shield, Send } from 'lucide-react';
import Magnetic from '../components/Magnetic';

const ScrambleText = ({ text, delay = 0, duration = 40 }: { text: string; delay?: number; duration?: number }) => {
  const [displayText, setDisplayText] = useState("");
  const chars = "01<>-_\\/[]{}—=+*^?#ABCDEF";

  useEffect(() => {
    let frame = 0;
    const totalFrames = 30;
    
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setDisplayText(
          text
            .split("")
            .map((char, i) => {
              if (char === " ") return " ";
              if (frame > (totalFrames / text.length) * i) return char;
              return chars[Math.floor(Math.random() * chars.length)];
            })
            .join("")
        );
        frame++;
        if (frame > totalFrames) clearInterval(interval);
      }, duration);
      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timeout);
  }, [text, delay, duration]);

  return <>{displayText || text.replace(/./g, "_")}</>;
};

const HackerBackground: React.FC = () => {
  // We use a high-quality hacker-themed video. 
  // This source features flowing green code/binary data which fits the aesthetic.
  const videoSource = "https://assets.mixkit.co/videos/preview/mixkit-binary-code-numbers-in-green-31807-large.mp4";

  return (
    <div className="absolute inset-0 pointer-events-none -z-20 overflow-hidden">
      {/* Dynamic Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-screen"
        style={{ filter: 'contrast(1.2) brightness(0.8) hue-rotate(10deg)' }}
      >
        <source src={videoSource} type="video/mp4" />
      </video>

      {/* Perspective Grid - Adding depth and movement */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] [transform:perspective(1000px)_rotateX(60deg)_translateY(-100px)_scale(2.5)] animate-[grid_15s_linear_infinite]" />
      
      {/* Scanlines Overlay - CRT monitor effect */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.1)_50%),linear-gradient(90deg,rgba(255,0,0,0.03),rgba(0,255,0,0.01),rgba(0,0,255,0.03))] bg-[size:100%_3px,3px_100%] z-10 opacity-40 pointer-events-none" />
      
      {/* Vignette - Darkens corners to focus on content */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(9,9,11,0.9)_100%)]" />

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes grid {
          0% { background-position: 0 0; }
          100% { background-position: 0 40px; }
        }
      `}} />
    </div>
  );
};

const Home: React.FC = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -200]);
  const rotate = useTransform(scrollY, [0, 1000], [0, 45]);

  return (
    <div className="relative min-h-screen bg-background">
      <HackerBackground />
      
      {/* Dynamic Glow Blobs */}
      <div className="absolute top-0 left-0 w-full h-screen overflow-hidden -z-10 pointer-events-none">
        <motion.div 
          style={{ y: y1, rotate }}
          className="absolute -top-[20%] -left-[10%] w-[60%] h-[100%] bg-primary/10 blur-[150px] rounded-full"
        />
        <motion.div 
          style={{ y: y2 }}
          className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[80%] bg-accent/10 blur-[150px] rounded-full"
        />
      </div>

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-5xl relative z-10"
        >
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-2 bg-zinc-900/80 backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full mb-8 shadow-xl"
          >
            <span className="flex h-2 w-2 rounded-full bg-accent animate-pulse" />
            <span className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">Dual-Engine Development Agency</span>
          </motion.div>

          <h1 className="text-5xl md:text-8xl font-display font-bold leading-[1.05] tracking-tighter mb-8 min-h-[1.1em] md:min-h-[2.2em] relative">
            <ScrambleText text="Building the " />
            <span className="text-accent relative inline-block">
              <ScrambleText text="Infrastructure" delay={400} />
              <motion.span 
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 0.1, repeat: Infinity, repeatDelay: 3 }}
                className="absolute inset-0 bg-accent/20 blur-sm pointer-events-none"
              />
            </span>
            <br className="hidden md:block" />
            <ScrambleText text=" of the Future." delay={800} />
          </h1>

          <p className="text-lg md:text-xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            We architect systems where logic meets execution. From high-throughput APIs to intelligent automation bots, we build for performance.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center space-y-6 md:space-y-0 md:space-x-8">
            <Magnetic strength={0.2}>
              <Link 
                to="/contact" 
                className="group relative bg-white text-background px-10 py-4 rounded-full font-bold flex items-center space-x-3 hover:bg-accent transition-all interactive overflow-hidden shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-accent/40"
              >
                <span>Initiate project</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Magnetic>
            
            <motion.a 
              href="https://t.me/duostackdevs"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ 
                scale: [1, 1.05, 1.02, 1.05],
              }}
              transition={{
                scale: {
                  repeat: Infinity,
                  duration: 2,
                  ease: "easeInOut"
                }
              }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3 text-zinc-400 hover:text-accent transition-colors interactive font-medium bg-zinc-900/40 border border-white/5 px-6 py-4 rounded-full backdrop-blur-md"
            >
              <Send className="w-4 h-4" />
              <span>@duostackdevs</span>
            </motion.a>
          </div>
        </motion.div>
      </section>

      {/* Trust & Stats Section */}
      <section className="py-24 border-y border-white/5 bg-zinc-950/80 backdrop-blur-xl relative z-10">
        <div className="container mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12">
          {[
            { label: 'Protocols Shipped', value: '42' },
            { label: 'Systems Active', value: '18' },
            { label: 'Lines of Code', value: '1.2M' },
            { label: 'Latency avg', value: '<2ms' },
          ].map((stat, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center group"
            >
              <div className="text-4xl font-display font-bold text-white mb-2 group-hover:text-accent transition-colors">{stat.value}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] font-bold">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Main Feature Cards */}
      <section className="py-32 container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-10">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-6xl font-display font-bold mb-8 tracking-tighter">
              Engineering <span className="text-accent italic font-light">superior</span> digital foundations.
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed">
              We specialize in the heavy lifting. Our team provides the computational logic and technical infrastructure that powers the next generation of fintech, SaaS, and AI startups.
            </p>
          </div>
          <Link to="/services" className="text-accent font-bold flex items-center group interactive text-sm uppercase tracking-widest bg-accent/5 px-8 py-4 rounded-full border border-accent/20 hover:bg-accent hover:text-background transition-all">
            Full Service List <ArrowRight className="ml-3 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <ServiceCard 
            icon={<Bot className="w-8 h-8 text-accent" />}
            title="High-Frequency Automation"
            desc="Developing autonomous agents and trading infrastructure that executes decisions with sub-millisecond precision."
          />
          <ServiceCard 
            icon={<Code className="w-8 h-8 text-primary" />}
            title="Distributed Cloud Platforms"
            desc="Building resilient multi-region architectures that scale seamlessly to support millions of concurrent connections."
          />
          <ServiceCard 
            icon={<Shield className="w-8 h-8 text-emerald-400" />}
            title="Security-First Backend"
            desc="Implementation of zero-trust architectures and hardened API gateways to protect your most sensitive data assets."
          />
        </div>
      </section>
    </div>
  );
};

const ServiceCard = ({ icon, title, desc }: { icon: React.ReactNode, title: string, desc: string }) => {
  return (
    <motion.div
      whileHover={{ y: -12 }}
      className="p-12 bg-zinc-900/40 border border-white/5 rounded-[40px] hover:bg-zinc-900/70 hover:border-accent/30 transition-all duration-700 group relative overflow-hidden backdrop-blur-sm"
    >
      <div className="mb-10 p-5 bg-zinc-950 border border-white/5 rounded-3xl w-fit group-hover:scale-110 group-hover:rotate-3 transition-transform duration-700 shadow-2xl">
        {icon}
      </div>
      <h3 className="text-2xl font-bold mb-6 tracking-tight group-hover:text-accent transition-colors">{title}</h3>
      <p className="text-zinc-500 leading-relaxed text-sm">{desc}</p>
      
      {/* Decorative hover gradient */}
      <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-accent/5 blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
    </motion.div>
  );
}

export default Home;
